import React from 'react';
import { Button, Divider, Paper, Typography } from '@mui/material';
import { IncludeOptions } from 'lib/api/include';
import { IPageOptions } from 'lib/api/paging';
import logger from 'lib/logger';
import { TableSkeletonComponent } from 'ui/skeleton/TableSkeleton';
import { MuiTableScrollItems } from 'ui/table/MuiTable';
import { AudienceAddCustomersModal } from './modals/AudienceAddCustomersModal';
import { Audience, AudienceType } from './models/segmentModel';
import { SegmentUser } from './models/segmentUserModel';
import { segmentsApi } from './segmentsApi';
import { SegmentUserTable } from './SegmentUserTable';
import { ActionDialog } from 'ui/dialogs/ActionDialog';
import { isDefined } from 'lib/typeguards';
import { useSnackbar } from 'notistack';
import {
    MESSAGE_MARKETING_AUDIENCE_REMOVE_USER_ERROR,
    MESSAGE_MARKETING_AUDIENCE_REMOVE_USER_SUCCESS
} from 'config/messages';
import { AudienceCardHeader } from './utils/audiencesUI';

const pagingOptions: IPageOptions = { limit: 100, sort: '-expires' };
const includeOptions: IncludeOptions<keyof Pick<SegmentUser, 'userFullName'>> = { userFullName: true };

export interface SegmentUserTableCardComponentProps {
    segment: Audience;
    disabled?: boolean;
}

export const SegmentUserBetaTableCardComponent: React.FC<SegmentUserTableCardComponentProps> = ({
    segment,
    disabled
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const segmentId = segment._id;
    const [users, setUsers] = React.useState<SegmentUser[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [nextKey, setNextKey] = React.useState(undefined);
    const [mounted, setMounted] = React.useState(false);
    const [open, setOpen] = React.useState(false);

    const openModal = React.useCallback(() => setOpen(true), []);
    const closeModal = React.useCallback(() => setOpen(false), []);

    const handleScroll = React.useCallback(async () => {
        const {
            body: { items, page }
        } = await segmentsApi.getSegmentUsersPaged(segmentId, includeOptions, {
            startKey: nextKey,
            ...pagingOptions
        });
        setUsers([...users, ...items]);
        setNextKey(page.nextKey);
    }, [users, nextKey, segmentId]);

    const scrollItems: MuiTableScrollItems = {
        isMoreItems: !!nextKey && mounted,
        key: nextKey,
        onScroll: handleScroll
    };

    React.useEffect(() => {
        async function getInitialUsers() {
            try {
                const {
                    body: { items, page }
                } = await segmentsApi.getSegmentUsersPaged(segmentId, includeOptions, pagingOptions);
                setUsers(items);
                setNextKey(page.nextKey);
            } catch (error) {
                logger.error('Expected users info but got', error);
            }
            setLoading(false);
            setMounted(true);
        }
        getInitialUsers();
    }, [segmentId]);

    const [userIdToDelete, setUserIdToDelete] = React.useState<string>();
    const [userDeleteLoading, setUserDeleteLoading] = React.useState(false);

    const handleDeleteClick = React.useCallback((userId: string) => {
        setUserIdToDelete(userId);
    }, []);
    const handleCancelDelete = React.useCallback(() => {
        setUserIdToDelete(undefined);
    }, []);

    const userToDelete = React.useMemo(() => {
        if (isDefined(userIdToDelete)) {
            return users.find(item => item.userId === userIdToDelete);
        }
        return undefined;
    }, [userIdToDelete, users]);

    const handleDeleteUser = React.useCallback(async () => {
        setUserDeleteLoading(true);
        const result = await segmentsApi.removeFromSegment(segmentId, userIdToDelete);
        if (!result.ok) {
            enqueueSnackbar(MESSAGE_MARKETING_AUDIENCE_REMOVE_USER_ERROR, { variant: 'error' });
        }
        enqueueSnackbar(MESSAGE_MARKETING_AUDIENCE_REMOVE_USER_SUCCESS, { variant: 'success' });
        setUserIdToDelete(undefined);
        setUsers(oldUsers => oldUsers.filter(item => item.userId !== userIdToDelete));
        setUserDeleteLoading(false);
    }, [segmentId, userIdToDelete, enqueueSnackbar]);

    return (
        <React.Fragment>
            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                {loading && <TableSkeletonComponent tableHeadColumns={2} tableBodyRows={4} />}
                {!loading && (
                    <React.Fragment>
                        <AudienceCardHeader>
                            <Typography>Customers</Typography>
                            <Button
                                onClick={openModal}
                                color="primary"
                                variant="contained"
                                disabled={disabled}
                                size="small"
                            >
                                Add Customer
                            </Button>
                        </AudienceCardHeader>
                        <Divider />
                        <SegmentUserTable
                            scroll={scrollItems}
                            data={users}
                            includeExpiry={segment.type !== AudienceType.PERSISTENT}
                            onDelete={handleDeleteClick}
                        />
                    </React.Fragment>
                )}
            </Paper>
            <AudienceAddCustomersModal
                open={open}
                onClose={closeModal}
                customers={users}
                segmentId={segmentId}
            />
            <ActionDialog
                title={`Remove ${userToDelete?.userFullName}?`}
                content="To remove the customer from this audience, type 'REMOVE' in the text input field."
                onClose={handleCancelDelete}
                onConfirm={handleDeleteUser}
                protectionWord="REMOVE"
                open={!!userToDelete}
                submitLabel="Submit"
                loading={userDeleteLoading}
            />
        </React.Fragment>
    );
};
